import { useState } from "react"
import { Button, CircularProgress } from "@mui/material"
import { Download } from "lucide-react"
import { financialApi } from "@crm-src/lib/api"
import { saveBlob } from "../../../lib/download"
import { formatCurrency } from "@crm-src/lib/utils"
import { useToast } from "@crm-src/hooks/use-toast"
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { PDFExportData } from "@crm-src/types/financial-metrics"

interface PDFExportButtonProps {
  period: string
  userId?: string
  variant?: "default" | "outline"
}

// Extend jsPDF types for autoTable
interface jsPDFWithAutoTable extends jsPDF {
  autoTable: (options: any) => jsPDF
}

export function PDFExportButton({ period, userId, variant = "default" }: PDFExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false)
  const { toast } = useToast()

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit'
    })
  }

  const generatePDF = async (data: PDFExportData) => {
    const doc = new jsPDF('portrait', 'mm', 'a4') as jsPDFWithAutoTable

    // Manually attach autoTable if not available
    if (typeof doc.autoTable !== 'function') {
      (doc as any).autoTable = (options: any) => {
        autoTable(doc, options)
        return doc
      }
    }

    const pageWidth = 210
    const margin = 15
    let yPos = margin

    // Header
    doc.setFontSize(20)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(37, 99, 235) // Blue color
    doc.text('Financial Metrics Report', margin, yPos)
    yPos += 10

    // Company and metadata
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(100, 100, 100)
    doc.text(`${data.company.name}`, margin, yPos)
    yPos += 5
    doc.text(`Generated on: ${formatDate(data.company.generated_at)}`, margin, yPos)
    yPos += 5
    doc.text(`Generated by: ${data.company.generated_by}`, margin, yPos)
    yPos += 5
    doc.text(`Period: ${data.date_range.formatted || `${data.date_range.start_date} to ${data.date_range.end_date}`}`, margin, yPos)
    yPos += 10

    // Divider line
    doc.setDrawColor(200, 200, 200)
    doc.line(margin, yPos, pageWidth - margin, yPos)
    yPos += 10

    if (data.report_type === 'salesperson' && data.performance) {
      // Salesperson Report
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text(`Performance Report: ${data.salesperson?.name}`, margin, yPos)
      yPos += 10

      // Performance Metrics
      const performanceData = [
        ['Total Revenue', formatCurrency(data.performance.total_revenue)],
        ['Deals Closed', data.performance.total_deals_closed.toString()],
        ['Average Deal Size', formatCurrency(data.performance.average_deal_size)],
        ['Quotes Created', data.performance.total_quotes_created.toString()],
        ['Quotes Converted', data.performance.quotes_converted.toString()],
        ['Conversion Rate', `${data.performance.conversion_rate}%`]
      ]

      doc.autoTable({
        startY: yPos,
        head: [['Metric', 'Value']],
        body: performanceData,
        theme: 'striped',
        headStyles: { fillColor: [37, 99, 235] },
        margin: { left: margin, right: margin }
      })

      yPos = (doc as any).lastAutoTable.finalY + 10

      // Monthly Breakdown
      if (data.monthly_breakdown && data.monthly_breakdown.length > 0) {
        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.text('Monthly Breakdown', margin, yPos)
        yPos += 7

        const monthlyData = data.monthly_breakdown.map(m => [
          m.month,
          formatCurrency(m.revenue),
          m.deals_closed.toString(),
          m.quotes_created.toString()
        ])

        doc.autoTable({
          startY: yPos,
          head: [['Month', 'Revenue', 'Deals Closed', 'Quotes Created']],
          body: monthlyData,
          theme: 'grid',
          headStyles: { fillColor: [37, 99, 235] },
          margin: { left: margin, right: margin }
        })
      }
    } else if (data.report_type === 'overview' && data.summary) {
      // Overview Report
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text('Financial Overview', margin, yPos)
      yPos += 10

      // Revenue Summary
      const revenueData = [
        ['Total Revenue', formatCurrency(data.summary.revenue.total)],
        ['Previous Period', formatCurrency(data.summary.revenue.previous_period)],
        ['Growth', `${data.summary.revenue.growth_percentage}%`]
      ]

      doc.autoTable({
        startY: yPos,
        head: [['Revenue Metrics', 'Value']],
        body: revenueData,
        theme: 'striped',
        headStyles: { fillColor: [37, 99, 235] },
        margin: { left: margin, right: margin }
      })

      yPos = (doc as any).lastAutoTable.finalY + 10

      // Quote Statistics
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('Quote Statistics', margin, yPos)
      yPos += 7

      const quoteData = [
        ['Pending', data.summary.quotes.pending.count.toString(), formatCurrency(data.summary.quotes.pending.total_amount)],
        ['Converted', data.summary.quotes.converted.count.toString(), formatCurrency(data.summary.quotes.converted.total_amount)],
        ['Expired', data.summary.quotes.expired.count.toString(), formatCurrency(data.summary.quotes.expired.total_amount)],
        ['Rejected', data.summary.quotes.rejected.count.toString(), formatCurrency(data.summary.quotes.rejected.total_amount)],
        ['Total', data.summary.quotes.total.count.toString(), formatCurrency(data.summary.quotes.total.total_amount)]
      ]

      doc.autoTable({
        startY: yPos,
        head: [['Status', 'Count', 'Amount']],
        body: quoteData,
        theme: 'grid',
        headStyles: { fillColor: [37, 99, 235] },
        margin: { left: margin, right: margin }
      })

      yPos = (doc as any).lastAutoTable.finalY + 10

      // Expected Future Income
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('Expected Future Income', margin, yPos)
      yPos += 7

      const incomeData = [
        ['Unpaid Invoices', formatCurrency(data.summary.expected_future_income.unpaid_invoices)],
        ['Partial Payments Remaining', formatCurrency(data.summary.expected_future_income.partial_payments_remaining)],
        ['Pending Quotes', formatCurrency(data.summary.expected_future_income.pending_quotes)],
        ['Total Expected', formatCurrency(data.summary.expected_future_income.total)]
      ]

      doc.autoTable({
        startY: yPos,
        head: [['Category', 'Amount']],
        body: incomeData,
        theme: 'striped',
        headStyles: { fillColor: [37, 99, 235] },
        margin: { left: margin, right: margin }
      })

      yPos = (doc as any).lastAutoTable.finalY + 10

      // Salesperson Performance (if admin)
      if (data.salespeople_performance && data.salespeople_performance.length > 0) {
        // Check if we need a new page
        if (yPos > 220) {
          doc.addPage()
          yPos = margin
        }

        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.text('Salesperson Performance', margin, yPos)
        yPos += 7

        const salesData = data.salespeople_performance.map(s => [
          s.rank.toString(),
          s.user_name,
          formatCurrency(s.total_revenue),
          s.total_deals_closed.toString(),
          `${s.conversion_rate}%`
        ])

        doc.autoTable({
          startY: yPos,
          head: [['Rank', 'Salesperson', 'Revenue', 'Deals', 'Conv. Rate']],
          body: salesData,
          theme: 'grid',
          headStyles: { fillColor: [37, 99, 235] },
          margin: { left: margin, right: margin },
          columnStyles: {
            0: { cellWidth: 15 },
            2: { cellWidth: 30 },
            3: { cellWidth: 20 },
            4: { cellWidth: 25 }
          }
        })
      }
    }

    // Save the PDF
    const filename = `Financial_Report_${period}_${new Date().toISOString().split('T')[0]}.pdf`
    // Native OS Save-As dialog in the desktop app; browser download on the web.
    await saveBlob(filename, doc.output('blob') as Blob)
  }

  const handleExport = async () => {
    try {
      setIsExporting(true)

      // Fetch export data from backend
      const response = await financialApi.exportPDF({ period, user_id: userId })
      const data: PDFExportData = response.data

      // Generate PDF
      await generatePDF(data)

      toast({
        title: "PDF Exported",
        description: "Financial report has been downloaded successfully",
      })
    } catch (error: any) {
      console.error('Error exporting PDF:', error)
      toast({
        title: "Export Failed",
        description: error.response?.data?.error || "Failed to export PDF",
        variant: "destructive",
      })
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Button
      variant={variant === "outline" ? "outlined" : "contained"}
      color={variant === "outline" ? "inherit" : "primary"}
      onClick={handleExport}
      disabled={isExporting}
      startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <Download size={16} />}
    >
      {isExporting ? "Exporting..." : "Export PDF"}
    </Button>
  )
}





